import { Typography } from '@mui/material';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ConnectedRoleAutocomplete, RoleAutocomplete, SpecialtyAutocomplete } from 'shared/components';
import { useFeatureEnabled } from 'shared/hooks';
import { Role } from 'shared/types';

import { ReasonInputBaseProps } from './BaseInput';
import FieldFreeText from './FieldFreeText';

export default function RoleInput(props: ReasonInputBaseProps) {
    const { t } = useTranslation('feedback', { keyPrefix: 'declineDialog.setReasonDetails.reasonInput.role' });
    const { control, setValue } = props;
    const isRoleSpecialtyEnabled = useFeatureEnabled('RoleSpecialtyTaxonomy');
    const role = useWatch({ control, name: 'reasonInput.role' });
    const roleId = role?.id ?? null;

    if (!isRoleSpecialtyEnabled) {
        return (
            <>
                <Typography variant="subtitle2">{ t('label') }</Typography>
                <ConnectedRoleAutocomplete control={ control }
                    name="reasonInput.role" />
                <FieldFreeText control={ control } />
            </>
        );
    }

    return (
        <>
            <Typography variant="subtitle2">{ t('label') }</Typography>
            <Controller
                control={ control }
                name="reasonInput.role"
                render={ ({ field: { ref, value, onChange } }) => (
                    <RoleAutocomplete
                        value={ value ?? null }
                        onChange={ (next: Role | null) => {
                            onChange(next);
                            // Speciality is role-scoped — reset it when the role changes.
                            setValue('reasonInput.specialty', null);
                        } }
                        inputRef={ ref }
                    />
                ) }
            />

            <Typography variant="subtitle2">{ t('specialtyLabel') }</Typography>
            <Controller
                control={ control }
                name="reasonInput.specialty"
                render={ ({ field: { ref, value, onChange } }) => (
                    <SpecialtyAutocomplete
                        // Remount on role change so the role-scoped options preload fresh.
                        key={ roleId ?? 'none' }
                        roleId={ roleId }
                        value={ value ?? null }
                        onChange={ onChange }
                        inputRef={ ref }
                        preloadOnMount={ false }
                        preloadOnOpen
                    />
                ) }
            />
            <FieldFreeText control={ control } />
        </>
    );
}
